import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Search,
  Add,
  Edit,
  Delete,
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import apiService from '../services/apiService';

const DocumentsPage = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await apiService.getDocumentTypes();

      if (response.success) {
        setDocuments(response.data);
        setError('');
      } else {
        setError(response.message || 'Failed to load document types');
        setDocuments([]);
      }
    } catch (err) {
      console.error('Documents error:', err);
      setError('Failed to load document types. Please check your connection and try again.');
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      name: '',
      required: 'yes',
      type: 'driver',
      expiry_date: 'no',
      status: 'active',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Document name is required'),
      required: Yup.string().required('Required field is mandatory'),
      type: Yup.string().required('Type is required'),
      expiry_date: Yup.string().required('Expiry Date field is mandatory'),
      status: Yup.string().required('Status is required'),
    }),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        if (editMode) {
          const response = await apiService.updateDocumentType(selectedDocument.id, values);
          if (response.success) {
            alert('Document type updated successfully');
            setOpenDialog(false);
            resetForm();
            fetchDocuments();
          } else {
            alert(response.message || 'Failed to update document type');
          }
        } else {
          const response = await apiService.addDocumentType(values);
          if (response.success) {
            alert('Document type added successfully');
            setOpenDialog(false);
            resetForm();
            fetchDocuments();
          } else {
            alert(response.message || 'Failed to add document type');
          }
        }
      } catch (err) {
        console.error('Document operation error:', err);
        alert(`Failed to ${editMode ? 'update' : 'add'} document type`);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleAddDocument = () => {
    setEditMode(false);
    setSelectedDocument(null);
    formik.resetForm();
    setOpenDialog(true);
  };

  const handleEditDocument = (document) => {
    setEditMode(true);
    setSelectedDocument(document);
    formik.setValues({
      name: document.name || '',
      required: document.required ? 'yes' : 'no',
      type: document.type || 'driver',
      expiry_date: document.expiry_date ? 'yes' : 'no',
      status: document.status || 'active',
    });
    setOpenDialog(true);
  };

  const handleDeleteDocument = async (id) => {
    if (window.confirm('Are you sure you want to delete this document type?')) {
      try {
        const response = await apiService.deleteDocumentType(id);
        if (response.success) {
          alert('Document type deleted successfully');
          fetchDocuments();
        } else {
          alert(response.message || 'Failed to delete document type');
        }
      } catch (err) {
        console.error('Delete error:', err);
        alert('Failed to delete document type');
      }
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    formik.resetForm();
    setSelectedDocument(null);
  };

  const getStatusChip = (status) => {
    return status === 'active' ? (
      <Chip label="Active" color="success" size="small" />
    ) : (
      <Chip label="Inactive" color="default" size="small" />
    );
  };

  const getRequiredChip = (required) => {
    return required ? (
      <Chip label="Yes" color="error" size="small" />
    ) : (
      <Chip label="No" color="default" size="small" />
    );
  };

  const getTypeChip = (type) => {
    const colorMap = {
      driver: 'primary',
      passenger: 'secondary',
      vehicle: 'info',
    };
    return <Chip label={type || 'driver'} color={colorMap[type] || 'default'} size="small" />;
  };

  const getExpiryDateChip = (expiryDate) => {
    return expiryDate ? (
      <Chip label="Yes" color="warning" size="small" />
    ) : (
      <Chip label="No" color="default" size="small" />
    );
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Document Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 120,
      renderCell: (params) => getTypeChip(params.row.type),
    },
    {
      field: 'required',
      headerName: 'Required',
      width: 100,
      renderCell: (params) => getRequiredChip(params.row.required),
    },
    {
      field: 'expiry_date',
      headerName: 'Expiry Date',
      width: 120,
      renderCell: (params) => getExpiryDateChip(params.row.expiry_date),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => getStatusChip(params.row.status),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 160,
      valueGetter: (params) => params.row.created_at ? new Date(params.row.created_at).toLocaleString() : '-',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" color="primary" onClick={() => handleEditDocument(params.row)}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleDeleteDocument(params.row.id)}>
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const filteredDocuments = documents.filter((doc) =>
    doc.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Document Types
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage document types that drivers need to upload
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddDocument}
          sx={{
            bgcolor: '#FFC107',
            color: '#000',
            '&:hover': {
              bgcolor: '#FFD54F',
            },
          }}
        >
          Add Document
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Search document types..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />

          <DataGrid
            rows={filteredDocuments}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            autoHeight
            loading={loading}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Document Type' : 'Add Document Type'}
        </DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Document Name"
                  name="name"
                  placeholder="e.g., Driver's License, RC Book, Insurance"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    name="type"
                    value={formik.values.type}
                    onChange={formik.handleChange}
                    label="Type"
                  >
                    <MenuItem value="driver">Driver</MenuItem>
                    <MenuItem value="passenger">Passenger</MenuItem>
                    <MenuItem value="vehicle">Vehicle</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Required</InputLabel>
                  <Select
                    name="required"
                    value={formik.values.required}
                    onChange={formik.handleChange}
                    label="Required"
                  >
                    <MenuItem value="yes">Yes</MenuItem>
                    <MenuItem value="no">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Expiry Date</InputLabel>
                  <Select
                    name="expiry_date"
                    value={formik.values.expiry_date}
                    onChange={formik.handleChange}
                    label="Expiry Date"
                  >
                    <MenuItem value="yes">Yes</MenuItem>
                    <MenuItem value="no">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={formik.values.status}
                    onChange={formik.handleChange}
                    label="Status"
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={formik.isSubmitting}
              sx={{
                bgcolor: '#FFC107',
                color: '#000',
                '&:hover': {
                  bgcolor: '#FFD54F',
                },
              }}
            >
              {editMode ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default DocumentsPage;
